/**
 * Skeleton component for my team page loading state
 * @module components/common/MyTeamSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * My team loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the my team page
 */
export const MyTeamSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between">
        <div className="space-y-2">
          <SkeletonText width={200} height={36} />
          <SkeletonText width={350} height={16} />
        </div>
        <SkeletonRounded width={100} height={36} />
      </div>

      {/* Team Metrics Cards */}
      <div className="dashboard-grid lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <SkeletonText width={120} height={14} />
                <SkeletonText width={60} height={32} />
              </div>
              <SkeletonCircular width={48} height={48} />
            </div>
          </Card>
        ))}
      </div>

      {/* View Mode Toggle Card */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <SkeletonText width={180} height={20} />
          <div className="flex gap-2">
            <SkeletonRounded width={80} height={32} />
            <SkeletonRounded width={100} height={32} />
          </div>
        </div>
      </Card>

      {/* Direct Reports List */}
      <Card className="p-6">
        <SkeletonText width={200} height={24} className="mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-center gap-3">
                <SkeletonCircular width={48} height={48} />
                <div className="flex-1 min-w-0 space-y-2">
                  <SkeletonText width="80%" height={16} />
                  <SkeletonText width="60%" height={14} />
                  <SkeletonText width="50%" height={12} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-dark-lighter flex gap-2">
                <SkeletonRounded width="50%" height={32} />
                <SkeletonRounded width="50%" height={32} />
              </div>
            </Card>
          ))}
        </div>
      </Card>
    </div>
  )
}
